// Slippage: the delivery's (how far below the quote a fill may land) and, for a hosted swap, the
// source swap's.

import { useState } from "react";
import { SLIPPAGE_MAX_BPS, SLIPPAGE_PRESETS, SRC_SLIPPAGE_PRESETS, fmtPct, parseSlippage } from "../swap-form";
import { Modal } from "./Modal";

type Props = { slippage: number; setSlippage: (b: number) => void; srcSlippage: number | null; setSrcSlippage: (b: number | null) => void; swapping: boolean; onClose: () => void };

export function Settings({ slippage, setSlippage, srcSlippage, setSrcSlippage, swapping, onClose }: Props) {
  const custom = !SLIPPAGE_PRESETS.includes(slippage);
  const [text, setText] = useState(custom ? String(slippage / 100) : "");
  const parsed = text ? parseSlippage(text) : null;
  return (
    <Modal title="Settings" onClose={onClose}>
      <section className="setting">
        <div className="setting-head">
          <h3>Max slippage</h3>
          <span className="muted">{fmtPct(slippage)}</span>
        </div>
        <p className="fine">The least you accept below the quoted amount. If no solver can deliver at least that before the intent expires, you are refunded in full.</p>
        <div className="seg" role="group" aria-label="Max slippage">
          {SLIPPAGE_PRESETS.map((b) => (
            <button key={b} className={slippage === b ? "on" : ""} aria-pressed={slippage === b} onClick={() => (setSlippage(b), setText(""))}>
              {fmtPct(b)}
            </button>
          ))}
          <label className={`seg-input${custom ? " on" : ""}`}>
            <input
              inputMode="decimal"
              placeholder="Custom"
              value={text}
              aria-label="Custom slippage in percent"
              aria-invalid={!!text && parsed === null}
              onChange={(e) => {
                setText(e.target.value);
                const b = parseSlippage(e.target.value);
                if (b !== null) setSlippage(b);
              }}
            />
            <span>%</span>
          </label>
        </div>
        {text && parsed === null && <p className="field-error">Enter a percentage from 0.01 to {SLIPPAGE_MAX_BPS / 100}.</p>}
        {slippage > 300 && <p className="field-warn">High slippage: a fill can land well below the quote.</p>}
      </section>
      <section className="setting">
        <div className="setting-head">
          <h3>Swap slippage</h3>
          <span className="muted">{srcSlippage === null ? "Auto" : fmtPct(srcSlippage)}</span>
        </div>
        <p className="fine">
          When you pay with a token other than the chain's stable, it is swapped into the stable in the same transaction (OKX on EVM, Jupiter on Solana). This is that swap's tolerance; a refund comes back in the stable.
          {!swapping && " Not used for your current selection."}
        </p>
        <div className="seg" role="group" aria-label="Swap slippage">
          {SRC_SLIPPAGE_PRESETS.map((b) => {
            const v = b ?? null;
            return (
              <button key={String(b)} className={srcSlippage === v ? "on" : ""} aria-pressed={srcSlippage === v} onClick={() => setSrcSlippage(v)}>
                {b === undefined ? "Auto" : fmtPct(b)}
              </button>
            );
          })}
        </div>
      </section>
    </Modal>
  );
}
